/**
 * Shared report account/group selection logic.
 *
 * Single source of truth for the report popups' dropdown so the ~15 popups stop
 * copy-pasting (and mis-routing on) the `startsWith('@')` heuristic. See
 * docs/superpowers/specs/2026-06-24-report-groups-design.md.
 *
 * Conventions (design decisions D1/D2/D9):
 *  - The backend `/codenames` returns BARE codenames. The leading "@" is a
 *    *display* affix the UI renders for groups only — it is never part of a value.
 *  - Routing is by the item's real `type` ('group' | 'account'), never by "@".
 *  - The dropdown shows four sections: Groups, Families, Composites, and
 *    Accounts (the legacy "client" list is collapsed into Accounts, since every
 *    client is 1:1 with an account). A FAMILY is a client family from the PM's
 *    roster; it carries `type: 'group'` because it behaves exactly like one
 *    (same @/@@ modes, same generation params, same specific-account narrowing)
 *    and `kind: 'family'`, which only sectioning and ordering read. See
 *    docs/superpowers/specs/2026-07-29-report-generation-by-family-design.md. A
 *    COMPOSITE is the same shape again, `kind: 'composite'`, backed by
 *    lake-defined membership instead of a PM roster. See
 *    docs/superpowers/plans/2026-08-26-composite-report-generation.md.
 *  - Typed prefix sets the group generation mode:
 *      "@name"  -> sequential (a string of per-account reports)
 *      "@@name" -> aggregate  (one pooled report)
 */

/** Parse the typed search box into { mode, prefix, term }. */
export function parseQuery(raw) {
  const value = (raw || '').trim();
  if (value.startsWith('@@')) return { mode: 'aggregate', prefix: '@@', term: value.slice(2) };
  if (value.startsWith('@')) return { mode: 'sequential', prefix: '@', term: value.slice(1) };
  return { mode: null, prefix: '', term: value };
}

/** Normalize a group entry from the /codenames payload. */
function groupItem(g) {
  return {
    ...g,
    type: 'group',
    kind: 'group',
    members: g.members || [],
    displayName: `@${g.codename}${g.name ? ` — ${g.name}` : ''}`,
    sortKey: `0_${g.codename}`,
  };
}

/** Normalize a family entry (a client_groups row of kind 'family'). */
function familyItem(f) {
  return {
    ...f,
    // Behaviourally a group: @/@@ modes, group_codename params, account
    // narrowing. `kind` is what makes it its own dropdown section.
    type: 'group',
    kind: 'family',
    members: f.members || [],
    displayName: `@${f.codename}${f.name ? ` — ${f.name}` : ''}`,
    sortKey: `1_${f.codename}`,
  };
}

/** Normalize a composite entry (lake-defined, dated membership server-side). */
function compositeItem(c) {
  return {
    ...c,
    // Behaviourally a group: @/@@ modes, group_codename params, account
    // narrowing. `kind` gives it its own dropdown section.
    type: 'group',
    kind: 'composite',
    members: c.members || [],
    displayName: `@${c.codename}${c.name ? ` — ${c.name}` : ''}`,
    sortKey: `3_${c.codename}`,
  };
}

/**
 * Groups, families, then composites, all normalized - every "container" an
 * account can sit in. Single accessor so the list builder, the filter and the
 * account->container index can never disagree about what counts as a container.
 */
function containerItems(data) {
  return [
    ...(data.groups || []).map(groupItem),
    ...(data.families || []).map(familyItem),
    ...(data.composites || []).map(compositeItem),
  ];
}

/** Normalize an account entry. Collapses the legacy `clients` list too. */
function accountItem(a) {
  const acct = a.account_number;
  const label = a.client_name || a.name || '';
  return {
    ...a,
    type: 'account',
    account_number: acct,
    codename: acct,
    name: label,
    // Numeric market value (when /codenames supplies it) drives the ordering so
    // funded accounts surface above near-empty shells; 0 when absent.
    marketValue: Number(a.market_value) || 0,
    displayName: `${label || acct}${label ? ` (${acct})` : ''}`,
    sortKey: `2_${(label || acct).toUpperCase()}`,
  };
}

/**
 * Order accounts by market value DESC (funded first), then name/account ASC.
 * When the payload carries no market values every account ties at 0 and the
 * tiebreak reduces to the previous alphabetical-by-name order.
 */
function compareAccounts(a, b) {
  const mv = (b.marketValue || 0) - (a.marketValue || 0);
  if (mv !== 0) return mv;
  const an = (a.name || a.account_number || '').toUpperCase();
  const bn = (b.name || b.account_number || '').toUpperCase();
  return an.localeCompare(bn);
}

/**
 * Full, unfiltered item list: groups (by codename), then families (by
 * codename), then composites (by codename), then accounts (by market value).
 */
export function buildItemsList(data) {
  const byCodename = (a, b) => a.codename.localeCompare(b.codename);
  const groups = (data.groups || []).map(groupItem).sort(byCodename);
  const families = (data.families || []).map(familyItem).sort(byCodename);
  const composites = (data.composites || []).map(compositeItem).sort(byCodename);
  const accounts = (data.accounts || []).map(accountItem).sort(compareAccounts);
  return [...groups, ...families, ...composites, ...accounts];
}

/**
 * Index: account_number -> [group/family/composite items] it belongs to
 * (powers the fallback, so typing a member's codename offers its container).
 */
export function buildAccountGroupIndex(data) {
  const groups = containerItems(data);
  const idx = new Map();
  for (const g of groups) {
    for (const acct of g.members || []) {
      if (!idx.has(acct)) idx.set(acct, []);
      idx.get(acct).push(g);
    }
  }
  return idx;
}

function matchesAccount(item, t) {
  const lt = t.toLowerCase();
  return (
    (item.account_number || '').toLowerCase().includes(lt) ||
    (item.name || '').toLowerCase().includes(lt) ||
    // Match the client's shortname/codename (e.g. "SELBY", "BASSP") so typing a
    // family shortname surfaces accounts whose display name doesn't contain it.
    (item.client_codename || '').toLowerCase().includes(lt)
  );
}

/**
 * Filter the dropdown for the typed value, implementing the fallback:
 * when "@term"/"@@term" matches no group but matches account(s), return the
 * GROUPS those accounts belong to (so the dropdown lists where the account lives
 * instead of going blank). Each returned item carries `viaAccount` in that case.
 */
export function filterItems(data, raw, accountGroupIndex) {
  const { mode, term } = parseQuery(raw);
  // Groups, families AND composites: all are @-selectable containers, so all
  // participate in the codename/name match and in the account->container fallback.
  const groups = containerItems(data);
  const accounts = (data.accounts || []).map(accountItem);

  if (!term) return buildItemsList(data);

  const lt = term.toLowerCase();
  const groupHits = groups.filter(
    (g) => g.codename.toLowerCase().includes(lt) || (g.name || '').toLowerCase().includes(lt),
  );

  // Group-intent (typed "@"/"@@"): groups first; if none match, fall back to
  // "groups this account belongs to".
  if (mode) {
    if (groupHits.length > 0) return groupHits;
    const acctHits = accounts.filter((a) => matchesAccount(a, term));
    const seen = new Set();
    const fallbackGroups = [];
    for (const a of acctHits) {
      for (const g of accountGroupIndex.get(a.account_number) || []) {
        if (!seen.has(g.codename)) {
          seen.add(g.codename);
          fallbackGroups.push({ ...g, viaAccount: a.name || a.account_number });
        }
      }
    }
    return fallbackGroups; // may be [] -> caller shows "not in any group" hint
  }

  // Plain search: groups (by codename) first, then matching accounts (by market value).
  const acctHits = accounts.filter((a) => matchesAccount(a, term));
  return [
    ...groupHits.sort((a, b) => a.codename.localeCompare(b.codename)),
    ...acctHits.sort(compareAccounts),
  ];
}

/**
 * Resolve a typed search string to a single selection for "press Enter to
 * generate" (and the last-accessed autofill). Reuses the same ranked search the
 * dropdown shows, so the auto-picked item is exactly the one the PM sees on top.
 *
 * Returns { item, mode, ambiguous }:
 *  - item: the resolved normalized item, or null.
 *  - mode: 'sequential'|'aggregate'|null parsed from a leading @ / @@.
 *  - ambiguous: true when the text cleanly identifies a CLIENT that owns several
 *    accounts. The caller must NOT generate in that case - it opens the dropdown
 *    so the PM picks the specific account (we never silently pick one of several
 *    real accounts - that's the worst wrong-report hazard). See
 *    docs/superpowers/specs/2026-06-29-report-shortname-and-autofill-design.md.
 *
 * Priority: exact account (number/displayName) -> exact client identifier
 * (codename or full name; 1 account selects, >1 is ambiguous) -> top fuzzy
 * account match -> top candidate. Plain typing biases to accounts; groups need @.
 */
export function resolveTypedSelection(data, raw, accountGroupIndex) {
  const { mode, term } = parseQuery(raw);
  if (!term) return { item: null, mode, ambiguous: false };

  const t = term.toLowerCase();
  // Exact matches run against the FULL normalized list (filterItems only does
  // substring matching on number/name/codename, which would miss an exact
  // "Name (A100)" display string); the fuzzy fallback uses the ranked filter.
  const allItems = buildItemsList(data);
  const allAccounts = allItems.filter((i) => i.type === 'account');

  // Group intent (@ / @@): exact group wins, else the top-ranked candidate.
  if (mode) {
    const exactGroup = allItems.find(
      (i) => i.type === 'group' && (i.codename || '').toLowerCase() === t,
    );
    if (exactGroup) return { item: exactGroup, mode, ambiguous: false };
    const candidates = filterItems(data, raw, accountGroupIndex);
    return { item: candidates[0] || null, mode, ambiguous: false };
  }

  // 1. Exact account match (account number, or "Name (A100)" display) -> unique.
  const exactAccount = allAccounts.find(
    (a) =>
      (a.account_number || '').toLowerCase() === t ||
      (a.displayName || '').toLowerCase() === t,
  );
  if (exactAccount) return { item: exactAccount, mode, ambiguous: false };

  // 2. Exact client identifier (codename or full client name). One account ->
  //    select it; several -> ambiguous (open the dropdown).
  const byClient = allAccounts.filter(
    (a) =>
      (a.client_codename || '').toLowerCase() === t ||
      (a.name || '').toLowerCase() === t,
  );
  if (byClient.length === 1) return { item: byClient[0], mode, ambiguous: false };
  if (byClient.length > 1) return { item: null, mode, ambiguous: true };

  // 3. Fuzzy: top account match (ranked by market value), else top candidate.
  const candidates = filterItems(data, raw, accountGroupIndex);
  const fuzzyAccounts = candidates.filter((i) => i.type === 'account');
  if (fuzzyAccounts.length > 0) return { item: fuzzyAccounts[0], mode, ambiguous: false };
  return { item: candidates[0] || null, mode, ambiguous: false };
}

/**
 * Build the /fetch-data params for the chosen item.
 * @param selectedItem normalized item (type group|account)
 * @param opts { mode: 'sequential'|'aggregate', specificAccount }
 */
export function buildSelectionParams(selectedItem, opts = {}) {
  const params = {};
  if (!selectedItem) return params;
  if (selectedItem.type === 'group') {
    params.group_codename = selectedItem.codename; // bare
    params.group_mode = opts.mode === 'aggregate' ? 'aggregate' : 'sequential';
    params.portfolioType = 'group';
    if (opts.specificAccount && opts.specificAccount !== 'ALL') {
      params.specific_account = opts.specificAccount;
    }
  } else {
    params.account_number = selectedItem.account_number;
    if (selectedItem.client_codename) params.client_codename = selectedItem.client_codename;
    params.portfolioType = 'account';
  }
  return params;
}
